// src/pages/FulfillmentPage/components/FulfillmentTimeline.tsx
import React from "react";
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  Chip, 
  Avatar,
  alpha,
  useTheme,
  Fade,
  Slide,
  Paper
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon from "@mui/icons-material/Inventory";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CancelIcon from "@mui/icons-material/Cancel";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { FulfillmentEvent } from "../types";

interface FulfillmentTimelineProps {
  events: FulfillmentEvent[];
}

// Helper to get icon based on event type/status
const getEventIcon = (event: FulfillmentEvent) => {
  const type = event.event_type?.toUpperCase();
  const to = event.meta?.to?.toUpperCase();
  
  if (type === "STATUS") {
    if (to === "DELIVERED") return <CheckCircleIcon />;
    if (to === "SHIPPED" || to === "OUT_FOR_DELIVERY") return <LocalShippingIcon />;
    if (to === "READY") return <InventoryIcon />;
    if (to === "PACKING") return <PlayArrowIcon />;
    if (to === "CANCELLED") return <CancelIcon />;
  }
  return <ScheduleIcon />;
};

// Helper to get color based on event - with proper fallbacks
const getEventColor = (event: FulfillmentEvent, theme: any) => {
  const to = event.meta?.to?.toUpperCase();
  
  // Return the actual color palette object with main property
  if (to === "DELIVERED") return theme.palette.success;
  if (to === "SHIPPED" || to === "OUT_FOR_DELIVERY") return theme.palette.info;
  if (to === "READY") return theme.palette.warning;
  if (to === "PACKING") return theme.palette.primary;
  if (to === "CANCELLED") return theme.palette.error;
  
  // Default fallback - ensure it has a 'main' property
  return theme.palette.primary; // Use primary as safe default
};

// Format relative time (e.g., "2 hours ago", "yesterday")
const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return date.toLocaleDateString(undefined, { 
    month: 'short', 
    day: 'numeric',
    year: now.getFullYear() !== date.getFullYear() ? 'numeric' : undefined
  });
};

// Format message nicely
const formatEventMessage = (event: FulfillmentEvent) => {
  if (event.event_type === "STATUS") {
    const from = event.meta?.from || "?";
    const to = event.meta?.to || "?";
    
    // Special case for specific transitions
    if (to === "SHIPPED") {
      const tracking = event.meta?.tracking?.tracking_number;
      return tracking 
        ? `Shipped • Tracking: ${tracking}`
        : "Marked as shipped";
    }
    if (to === "OUT_FOR_DELIVERY") return "Out for delivery";
    if (to === "DELIVERED") return "Delivered";
    if (to === "READY") return "Ready for pickup/shipping";
    if (to === "PACKING") return "Packing started";
    
    return `Status: ${from} → ${to}`;
  }
  
  if (event.event_type === "NOTE_UPDATED") {
    return "Packing notes updated";
  }
  
  return event.event_type?.replace(/_/g, ' ') || "Event logged";
};

export const FulfillmentTimeline: React.FC<FulfillmentTimelineProps> = ({ events }) => {
  const theme = useTheme();
  
  // Safely handle undefined events array
  const safeEvents = events || [];
  const sortedEvents = [...safeEvents].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <Card 
      sx={{ 
        borderRadius: 3, 
        boxShadow: theme.shadows[2],
        width: "100%", 
        overflow: "hidden"
        // ❌ Removed hover effect on main card
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Stack 
          direction="row" 
          alignItems="center" 
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Timeline
            </Typography>
          </Stack>
          
          {sortedEvents.length > 0 && (
            <Chip
              label={`${sortedEvents.length} ${sortedEvents.length === 1 ? 'event' : 'events'}`}
              size="small"
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
          )}
        </Stack>

        {/* Timeline */}
        {sortedEvents.length === 0 ? (
          <Fade in={true}>
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                borderStyle: 'dashed'
              }}
            >
              <AccessTimeIcon 
                sx={{ 
                  fontSize: 48, 
                  color: theme.palette.text.disabled,
                  mb: 1
                }} 
              />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No events yet
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Events will appear as the fulfillment progresses
              </Typography>
            </Paper>
          </Fade>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {/* Vertical timeline line (hidden on mobile, shown on desktop) */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'block' },
                position: 'absolute',
                left: 20,
                top: 8,
                bottom: 8,
                width: 2,
                bgcolor: theme.palette.divider,
                zIndex: 0
              }}
            />
            
            <Stack spacing={2}>
              {sortedEvents.map((ev, index) => {
                const eventDate = new Date(ev.created_at);
                const eventColor = getEventColor(ev, theme);
                const EventIcon = getEventIcon(ev);
                
                // Safety check - ensure eventColor exists and has main property
                const safeColor = eventColor || theme.palette.primary;
                
                return (
                  <Slide 
                    direction="up" 
                    in={true} 
                    timeout={300 + index * 100}
                    key={ev.id}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      {/* Timeline dot with icon - kept hover effect */}
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: alpha(safeColor.main, 0.1),
                          color: safeColor.main,
                          border: `2px solid ${theme.palette.background.paper}`,
                          boxShadow: theme.shadows[2],
                          flexShrink: 0,
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        {React.cloneElement(EventIcon, { sx: { fontSize: 20 } })}
                      </Avatar>

                      {/* Event content - kept hover effect */}
                      <Paper
                        variant="outlined"
                        sx={{
                          flex: 1,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: theme.palette.background.paper,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                            borderColor: alpha(theme.palette.primary.main, 0.3)
                          }
                        }}
                      >
                        <Stack spacing={1}>
                          {/* Top row - event type and time */}
                          <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            spacing={1}
                          >
                            <Chip
                              size="small"
                              label={ev.event_type.replace(/_/g, ' ')}
                              sx={{
                                bgcolor: alpha(safeColor.main, 0.1),
                                color: safeColor.dark || safeColor.main,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                borderRadius: 1.5,
                                height: 24
                              }}
                            />
                            
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                whiteSpace: 'nowrap',
                                fontFamily: 'monospace',
                                bgcolor: alpha(theme.palette.background.default, 0.5),
                                px: 1,
                                py: 0.5,
                                borderRadius: 1
                              }}
                            >
                              {formatRelativeTime(eventDate)}
                            </Typography>
                          </Stack>

                          {/* Event message */}
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                              lineHeight: 1.5
                            }}
                          >
                            {formatEventMessage(ev)}
                          </Typography>

                          {/* Meta data (if any interesting meta to show) */}
                          {ev.meta?.note && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme.palette.text.secondary,
                                fontStyle: 'italic',
                                display: 'block',
                                mt: 0.5,
                                p: 1,
                                bgcolor: alpha(theme.palette.background.default, 0.5),
                                borderRadius: 1,
                                borderLeft: 2,
                                borderColor: safeColor.main
                              }}
                            >
                              Note: {ev.meta.note}
                            </Typography>
                          )}

                          {/* Exact timestamp (optional - shown on hover) */}
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: theme.palette.text.disabled,
                              display: 'block',
                              textAlign: 'right',
                              fontSize: '0.6rem',
                              transition: 'opacity 0.2s',
                              opacity: 0.7,
                              '&:hover': { opacity: 1 }
                            }}
                          >
                            {eventDate.toLocaleString()}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Box>
                  </Slide>
                );
              })}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};